// frontend/src/lib/tokenManager.ts
/**
 * Centralized Token Manager
 *
 * Single source of truth for authentication tokens. This module:
 * - Caches the current session from Supabase's onAuthStateChange
 * - Provides synchronous access to cached tokens
 * - Handles token refresh deduplication (prevents race conditions)
 * - Distinguishes between temporary and permanent refresh failures
 *
 * WHY THIS EXISTS:
 * Previously, multiple parts of the app (apiClient, chat/utils, AuthContext)
 * all called supabase.auth.getSession() independently. When a token expired,
 * concurrent calls could trigger multiple refresh attempts. Supabase returns
 * "refresh token already used" for concurrent refreshes, which was incorrectly
 * treated as a permanent failure, causing premature user signout.
 *
 * This module ensures:
 * 1. Only one refresh happens at a time (deduplication)
 * 2. Temporary errors (already used) are retried, not treated as fatal
 * 3. All consumers get tokens from the same cached source
 */

import type { Session, AuthChangeEvent } from '@supabase/supabase-js';

// =============================================================================
// TYPES
// =============================================================================

export interface TokenState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // Unix timestamp in seconds
}

type TokenListener = (state: TokenState) => void;

// =============================================================================
// STATE
// =============================================================================

let currentSession: Session | null = null;
let listeners: TokenListener[] = [];
let refreshPromise: Promise<Session | null> | null = null;
interface SupabaseAuthClient {
  getSession: () => Promise<{ data: { session: Session | null }; error: Error | null }>;
  refreshSession: () => Promise<{ data: { session: Session | null }; error: Error | null }>;
}

let supabaseClient: { auth: SupabaseAuthClient } | null = null;

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize the TokenManager with the Supabase client.
 * Call this once during app initialization, before any API calls.
 *
 * @param client - The Supabase client instance
 */
export const initializeTokenManager = (
  client: { auth: SupabaseAuthClient }
): void => {
  supabaseClient = client;
};

/**
 * Handle auth state changes from Supabase.
 * This should be called from the onAuthStateChange callback.
 *
 * @param event - The auth event type
 * @param session - The new session (or null if signed out)
 */
export const handleAuthStateChange = (event: AuthChangeEvent, session: Session | null): void => {
  currentSession = session;

  // Notify listeners of the state change
  const state = getTokenState();
  listeners.forEach(listener => {
    try {
      listener(state);
    } catch (e) {
      console.error('TokenManager listener error:', e);
    }
  });

  // Log state changes in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[TokenManager] Auth state changed:', {
      event,
      hasSession: !!session,
      expiresAt: session?.expires_at
        ? new Date(session.expires_at * 1000).toISOString()
        : null
    });
  }
};

// =============================================================================
// TOKEN ACCESS
// =============================================================================

/**
 * Get the current token state synchronously.
 * Returns cached values - does NOT trigger a refresh.
 */
export const getTokenState = (): TokenState => {
  return {
    accessToken: currentSession?.access_token || null,
    refreshToken: currentSession?.refresh_token || null,
    expiresAt: currentSession?.expires_at || null
  };
};

/**
 * Get a valid access token, refreshing if necessary.
 *
 * This is the main method consumers should use. It:
 * 1. Returns cached token if still valid
 * 2. Triggers refresh if token is expired or near expiry
 * 3. Deduplicates concurrent refresh requests
 * 4. Handles temporary errors with retry
 *
 * @returns The access token, or null if not authenticated
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  // If we have a valid cached token, return it immediately
  if (currentSession?.access_token && !isTokenExpiringSoon()) {
    return currentSession.access_token;
  }

  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    try {
      const session = await refreshPromise;
      return session?.access_token || null;
    } catch {
      return null;
    }
  }

  // No cached token and no refresh in progress - need to fetch
  return refreshAccessToken();
};

/**
 * Check if the current token is expired or will expire soon.
 * We consider tokens "expiring soon" if they expire within 60 seconds.
 */
const isTokenExpiringSoon = (): boolean => {
  if (!currentSession?.expires_at) return true;

  const now = Math.floor(Date.now() / 1000);
  const buffer = 60; // 60 seconds buffer
  return currentSession.expires_at - now < buffer;
};

/**
 * Refresh the access token with deduplication.
 *
 * Only one refresh can be in-flight at a time. Concurrent calls
 * will wait for the same promise.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  if (!supabaseClient) {
    console.error('[TokenManager] Not initialized - call initializeTokenManager first');
    return null;
  }

  // Create a new refresh promise
  refreshPromise = (async () => {
    try {
      const { data: { session }, error } = await supabaseClient.auth.getSession();

      if (error) {
        // Check if this is a temporary error (concurrent refresh)
        if (isTemporaryRefreshError(error)) {
          // Wait a bit and let Supabase's internal refresh complete
          await new Promise(resolve => setTimeout(resolve, 500));

          // Try once more - the other refresh should have completed
          const retryResult = await supabaseClient.auth.getSession();
          if (retryResult.data.session) {
            currentSession = retryResult.data.session;
            return retryResult.data.session;
          }
        }

        // Permanent error or retry failed
        console.error('[TokenManager] Refresh failed:', error.message);
        return null;
      }

      // Success - update cached session
      currentSession = session;
      return session;
    } catch (e) {
      console.error('[TokenManager] Refresh exception:', e);
      return null;
    } finally {
      // Clear the refresh promise so future calls can refresh again
      refreshPromise = null;
    }
  })();

  const session = await refreshPromise;
  return session?.access_token || null;
};

/**
 * Check if an error is a temporary refresh failure.
 *
 * "refresh token already used" happens when another request already
 * used the refresh token. This is temporary - we should wait and retry.
 *
 * "invalid refresh token" or "not found" are permanent - token is gone.
 */
const isTemporaryRefreshError = (error: Error | { message?: string }): boolean => {
  const message = (error?.message || '').toLowerCase();

  // Temporary: another request used the token first
  if (message.includes('already used')) {
    return true;
  }

  // Permanent errors
  // - invalid refresh token
  // - refresh token not found
  // - refresh_token_not_found
  return false;
};

/**
 * Check if an error is a permanent refresh failure that requires re-login.
 */
export const isPermanentRefreshError = (error: Error | { message?: string } | null): boolean => {
  if (!error) return false;

  const message = (error?.message || '').toLowerCase();

  return (
    message.includes('invalid refresh token') ||
    message.includes('refresh token not found') ||
    message.includes('refresh_token_not_found') ||
    message.includes('token has expired') ||
    message.includes('session not found')
  );
};

/**
 * Force-refresh the access token using Supabase's explicit refreshSession().
 *
 * Unlike getValidAccessToken (which uses getSession and may return a stale
 * cached session), this explicitly requests a new token from Supabase.
 * Used by the apiClient's 401 retry mechanism.
 */
export const forceRefreshToken = async (): Promise<string | null> => {
  if (!supabaseClient) return null;

  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    try {
      const session = await refreshPromise;
      return session?.access_token || null;
    } catch {
      return null;
    }
  }

  refreshPromise = (async () => {
    try {
      const { data: { session }, error } = await supabaseClient!.auth.refreshSession();
      if (error) {
        console.error('[TokenManager] Force refresh failed:', error.message);
        return null;
      }
      if (session) {
        currentSession = session;
      }
      return session;
    } catch (e) {
      console.error('[TokenManager] Force refresh exception:', e);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  const session = await refreshPromise;
  return session?.access_token || null;
};

// =============================================================================
// LISTENERS
// =============================================================================

/**
 * Subscribe to token state changes.
 *
 * @param listener - Callback function called when token state changes
 * @returns Unsubscribe function
 */
export const subscribeToTokenChanges = (listener: TokenListener): (() => void) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
};

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Clear the cached session.
 * Call this when the user signs out.
 */
export const clearTokenCache = (): void => {
  currentSession = null;
  refreshPromise = null;
};

/**
 * Check if we have a cached session (may be expired).
 * Useful for quick checks without triggering refresh.
 */
export const hasCachedSession = (): boolean => {
  return currentSession !== null;
};

/**
 * Check if the user appears to be authenticated.
 * Returns true if we have a non-expired token cached.
 */
export const isAuthenticated = (): boolean => {
  return currentSession !== null && !isTokenExpiringSoon();
};
